import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  borderRadius?: string;
  animate?: boolean;
}

/**
 * Skeleton Component
 * 
 * Displays a placeholder loading state with customizable dimensions and shape
 * Used to indicate content is loading to improve perceived performance
 * 
 * @param className - Additional CSS classes to apply
 * @param width - Width of the skeleton (default: '100%')
 * @param height - Height of the skeleton (default: '1rem')
 * @param borderRadius - Border radius of the skeleton (default: '0.25rem')
 * @param animate - Whether to animate the skeleton (default: true)
 */
const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width = '100%',
  height = '1rem',
  borderRadius = '0.25rem',
  animate = true,
}) => {
  return (
    <div
      className={`bg-[var(--background-inset)] ${animate ? 'animate-pulse' : ''} ${className}`}
      style={{
        width: width,
        height: height,
        borderRadius: borderRadius,
      }}
      role="status"
      aria-label="Loading..."
      data-testid="skeleton-loader"
    />
  );
};

/**
 * Skeleton Text Component
 * 
 * Displays a text block skeleton with multiple lines
 * 
 * @param lines - Number of lines to display (default: 3)
 * @param spacing - Spacing between lines (default: '0.5rem')
 * @param lastLineWidth - Width of the last line as a percentage (default: 80)
 * @param className - Additional CSS classes
 */
export const SkeletonText: React.FC<{
  lines?: number;
  spacing?: string;
  lastLineWidth?: number;
  className?: string;
}> = ({ 
  lines = 3, 
  spacing = '0.5rem', 
  lastLineWidth = 80,
  className = '' 
}) => {
  return (
    <div className={`space-y-[${spacing}] ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          height="0.8rem"
          width={index === lines - 1 && lastLineWidth < 100 ? `${lastLineWidth}%` : '100%'}
        />
      ))}
    </div>
  );
};

/**
 * Skeleton Card Component
 * 
 * Displays a card-shaped skeleton with optional header
 * 
 * @param height - Height of the card (default: '12rem')
 * @param hasHeader - Whether to include a header (default: true)
 * @param className - Additional CSS classes
 */
export const SkeletonCard: React.FC<{
  height?: string | number;
  hasHeader?: boolean;
  className?: string;
}> = ({ 
  height = '12rem', 
  hasHeader = true,
  className = '' 
}) => {
  return (
    <div 
      className={`bg-[var(--background-surface)] p-4 rounded-lg ${className}`}
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
    >
      {hasHeader && (
        <div className="flex items-center mb-4">
          <Skeleton width="2rem" height="2rem" borderRadius="9999px" className="mr-3" />
          <div>
            <Skeleton width="8rem" height="1rem" className="mb-2" />
            <Skeleton width="6rem" height="0.75rem" />
          </div>
        </div>
      )}
      <SkeletonText lines={4} />
    </div>
  );
};

/**
 * Skeleton Avatar Component
 * 
 * Displays a circle-shaped skeleton for avatar placeholders
 * 
 * @param size - Size of the avatar (default: '3rem')
 * @param className - Additional CSS classes
 */
export const SkeletonAvatar: React.FC<{
  size?: string | number;
  className?: string;
}> = ({ 
  size = '3rem',
  className = '' 
}) => {
  const sizeValue = typeof size === 'number' ? `${size}px` : size;
  
  return (
    <Skeleton 
      width={sizeValue} 
      height={sizeValue} 
      borderRadius="9999px" 
      className={className} 
    />
  );
};

export default Skeleton; 